import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { TrackingContext } from '@vrbo/react-event-tracking';
import PropTypes from 'prop-types';
import { ASYNC_FUNC, pageStates } from './PageConfig';
import { appendQueryParams } from '../../utils/StringUtil';
import AppActions from '../../actions/app';
import ExperimentUtil from '../../utils/ExperimentUtil';
import {
    isAgenticOnboardingPropertyEnabled,
    isArxRedirectEnabled,
    isConfirmAddressFeatureEnabled,
    isSharedSpaceDeflectionEnabled,
    isVrboUplEnabled
} from '../../utils/ExperimentsUtil';
import { langIdOnLocale } from '../../utils/LocaleUtil/languageList';

export const PageFlow = (WrappedComponent) => (class extends Component {
    displayNameToUse = '';

    // eslint-disable-next-line react/sort-comp
    static contextType = TrackingContext;

    static propTypes = {
        displayName: PropTypes.string,
        setDestination: PropTypes.func.isRequired,
        rootPath: PropTypes.string.isRequired,
        history: PropTypes.shape({
            push: PropTypes.func.isRequired
        }).isRequired,
        locale: PropTypes.shape({
            display: PropTypes.string.isRequired,
            canonical: PropTypes.string.isRequired
        }).isRequired,
        setDisableUFO: PropTypes.func.isRequired,
        eligibilityStatus: PropTypes.string.isRequired
    };

    static defaultProps = {
        displayName: undefined // We fall back to the class name if this is undefined
    };

    constructor(props) {
        super(props);

        this.getDisplayName = this.getDisplayName.bind(this);
        this.getCurrentPageState = this.getCurrentPageState.bind(this);
        this.getNextPageName = this.getNextPageName.bind(this);
        this.browseToNextPage = this.browseToNextPage.bind(this);
        this.setState = this.setState.bind(this);
        this.next = this.next.bind(this);
        this.nextPageLink = this.nextPageLink.bind(this);
        this.getLink = this.getLink.bind(this);
        this.setDisplayNameToUse = this.setDisplayNameToUse.bind(this);
        this.navigate = this.navigate.bind(this);
    }

    setDisplayNameToUse(displayName) {
        this.displayNameToUse = displayName;
    }

    getDisplayName() {
        const { displayName } = this.props;
        return displayName || WrappedComponent.displayName || WrappedComponent.name;
    }

    getCurrentPageState() {
        const pageName = this.displayNameToUse || this.getDisplayName();
        const currentPageState = pageStates.find((pageState) => pageState.name === pageName);
        if (!currentPageState) {
            throw new Error('Failed to get the current page state!');
        }
        return currentPageState;
    }

    getNextPageName() {
        const currentPageState = this.getCurrentPageState();
        const {
            setDestination
        } = this.props;
        // eslint-disable-next-line no-restricted-syntax
        for (const page of currentPageState.nextPages) {
            const { predicate, pageName, destination } = page;
            const result = this.evaluatePredicate(predicate);
            if (result) {
                if (destination) {
                    setDestination(destination);
                }
                return pageName;
            }
        }
        return '';
    }

    async getLink(pageName) {
        const nextPageState = pageStates.find((pageState) => pageState.name === pageName);
        const link = nextPageState ? nextPageState.link : '';
        if (link === ASYNC_FUNC) {
            const trueLink = await nextPageState.asyncLink(this.props);
            return trueLink;
        }
        return typeof link === 'function' ? link(this.props) : link;
    }

    evaluatePredicate(predicate) {
        // If there is no predicate, then we consider it always true.
        if (!predicate) return true;
        if (Array.isArray(predicate)) {
            // If the give us an empty list we consider it true, even though
            // that is bad form. To avoid a crash we handle it by
            // passing true to the reducer as the initial state.
            return predicate.map((x) => x(this.props)).reduce((acc, b) => acc && b, true);
        }
        return predicate(this.props);
    }

    browseToNextPage(link, withQueryParams = false) {
        const { trigger } = this.context;
        if (link === '') { return; }
        const PROTOCOL = 'http';
        window.dispatchEvent(new Event('pageflow.next'));
        if (link.indexOf(PROTOCOL) === -1) {
            const {
                history, rootPath, locale
            } = this.props;
            const { query } = this.props || {};
            let targetUrl = link;
            if (withQueryParams) {
                targetUrl = appendQueryParams(link, query);
            }
            const newPath = `/${locale.display}/${rootPath}/${targetUrl}`;
            history.push(newPath);
        } else {
            const isInternalPage = () => (link.includes('/estimate-results') || link.includes('/property-creation')) && (link.includes('/list/') || link.includes('/list-one-key/'));
            const { query } = this.props || {};
            const targetUrl = appendQueryParams(link, query);
            if (!isInternalPage) {
                trigger('genericEvent', {
                    category: 'handoff-target', action: 'handoff-transition', label: link
                });
            }
            window.location.assign(targetUrl);
        }
    }

    async nextPageLink() {
        const {
            getNextPageName,
            getLink
        } = this;
        const nextPageName = getNextPageName();
        // eslint-disable-next-line no-return-await
        return await getLink(nextPageName);
    }

    async next(pageToUse = '') {
        const {
            browseToNextPage
        } = this;
        if (pageToUse) {
            this.setDisplayNameToUse(pageToUse);
        }
        const nextPageLink = await this.nextPageLink();
        this.setDisplayNameToUse('');
        browseToNextPage(nextPageLink);
    }

    async navigate(pageName) {
        const {
            browseToNextPage,
            getLink
        } = this;
        const link = await getLink(pageName);
        browseToNextPage(link);
    }

    render() {
        const {
            next, nextPageLink, browseToNextPage, navigate, props
        } = this;
        const pageFlow = {
            next, nextPageLink, browseToNextPage, navigate
        };
        return <WrappedComponent {...props} pageFlow={pageFlow} />;
    }
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) => ({
    setDestination: (destination) => dispatch(AppActions.setDestination(destination))
});

/* istanbul ignore next */
const mapStateToProps = (state, ownProps) => {
    const {
        app, lead, location, classification, google, joinDetails
    } = state;
    const {
        addressValidation,
        latLng,
        geocoderValidation,
        unitNumberValidation,
        address,
        serverAddressValidation,
        geocoderLatLng,
        partialMatch
    } = location;
    const {
        missingInfo, joinId, autoAuthUrl, eligibilityStatus, propertyId, isPaperContractUser
    } = lead;
    const { isSelfServe } = joinDetails;
    const { hasPhoneNumber, hasPasswordSet } = missingInfo || {};
    const experimentUtil = new ExperimentUtil(app.experiments);
    const isCasDeprecated = true;
    const unitCheckEnabled = app.features.isFrboFlow;
    const isAddressV2 = app.features.isConventionalLodgingFlow || isConfirmAddressFeatureEnabled(experimentUtil);

    const { placeId } = google;
    const langId = langIdOnLocale(app.locale.canonical);
    const { isLoggedIn, rootPath, locale } = app;
    const isEstimatePageRemoved = experimentUtil.getExperimentDataByName('ULX_MKTING_MIGRATION')?.value === 1 ?? false;
    const relativePath = ownProps.location?.pathname?.replace(new RegExp(`/${locale.display}/${rootPath}`), '');
    const isAAP = relativePath?.includes('add-property');
    const isAgenticOnboardingProperty = isAgenticOnboardingPropertyEnabled(experimentUtil);
    const isVrboUpl = isVrboUplEnabled(experimentUtil);
    const isArxRedirect = isArxRedirectEnabled(experimentUtil);
    const isSharedSpaceDeflection = isSharedSpaceDeflectionEnabled(experimentUtil);
    return {
        isArxRedirect,
        isSharedSpaceDeflection,
        isVrboUpl,
        isAAP,
        isAgenticOnboardingProperty,
        isEstimatePageRemoved,
        isSelfServe,
        isPaperContractUser,
        serverAddressValidation,
        isAddressV2,
        langId,
        placeId,
        geocoderValidation,
        rootPath: app.rootPath,
        locale: app.locale,
        query: app.query,
        experiments: app.experiments,
        createdPropertyTriad: app.createdPropertyTriad,
        expediaId: app.expediaId,
        isLoggedIn,
        loggedInUserType: app.loggedInUserType,
        hasAdvertiserForSite: app.hasAdvertiserForSite,
        hasPasswordSet,
        hasPhoneNumber,
        addressValidation,
        siteName: app.siteName,
        features: app.features,
        joinId,
        classification,
        latLng,
        autoAuthUrl,
        isCasDeprecated,
        unitNumberValidation,
        unitCheckEnabled,
        eligibilityStatus,
        address,
        pathSource: app.pathSource,
        geocoderLatLng,
        partialMatch,
        userCountryCode: app.userCountryCode,
        propertyId
    };
};

export default compose(connect(mapStateToProps, mapDispatchToProps), PageFlow);
